/**
 * 共通HTTPリクエスト処理を担当するベースAPIクライアント
 * 全てのAPIクライアントの基底クラスとして機能
 */

import { getEnvironmentConfig } from '../config/apiEndpoints.js';

/**
 * ベースAPIクライアントクラス
 * HTTPリクエスト処理、エラーハンドリング、タイムアウト処理を提供
 */
class BaseApiClient {
  constructor(config = {}) {
    this._initializeConfig(config);
    this._setupLogging();
    // CSRFトークンのキャッシュ
    this._csrfToken = null;
  }

  /**
   * 設定を初期化
   * @private
   * @param {Object} config - カスタム設定
   */
  _initializeConfig(config) {
    const defaultConfig = getEnvironmentConfig();
    
    this.baseUrl = config.baseUrl !== undefined ? config.baseUrl : defaultConfig.baseUrl;
    this.endpoints = {
      ...defaultConfig.endpoints,
      ...config.endpoints
    };
    this.timeout = config.timeout || defaultConfig.timeout || 10000;
    this.environment = config.environment || defaultConfig.environment;
  }

  /**
   * ログ設定を初期化
   * @private
   */
  _setupLogging() {
    this.clientName = this.constructor.name || 'API_CLIENT';
    this.logPrefix = `[${this.clientName.toUpperCase()}]`;

  }

  /**
   * CSRFトークンを取得
   * @private
   * @returns {Promise<string|null>} CSRFトークン
   */
  async _fetchCsrfToken() {
    try {
      
      const url = this._buildUrl(this.endpoints.CSRF.TOKEN);
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include', // Cookieを含める
        headers: {
          'Content-Type': 'application/json',
          'X-Environment': this.environment
        }
      });

      if (!response.ok) {
        console.error(`${this.logPrefix} Failed to fetch CSRF token:`, response.status);
        return null;
      }

      const data = await response.json();
      
      // 様々なレスポンス形式に対応
      let token = null;
      
      if (typeof data === 'string') {
        // レスポンスが直接文字列の場合
        token = data;
      } else if (data && typeof data === 'object') {
        // オブジェクトの場合、様々なプロパティ名をチェック
        token = data.csrfToken || data.token || data.csrf || data._csrf || null;
      }
      
      if (token && typeof token === 'string') {
        this._csrfToken = token;
      } else {
        this._csrfToken = null;
      }

      return this._csrfToken;
    } catch (error) {
      console.error(`${this.logPrefix} Error fetching CSRF token:`, error);
      return null;
    }
  }

  /**
   * CSRFトークンを取得（キャッシュ付き）
   * @private
   * @returns {Promise<string|null>} CSRFトークン
   */
  async _getCsrfToken() {
    // キャッシュがあればそれを返す
    if (this._csrfToken) {
      return this._csrfToken;
    }

    // キャッシュがなければ取得
    return await this._fetchCsrfToken();
  }

  /**
   * CSRFトークンをクリア（エラー時などに使用）
   * @private
   */
  _clearCsrfToken() {
    this._csrfToken = null;
  }

  /**
   * リクエストURL生成
   * @private
   * @param {string} endpoint - エンドポイント
   * @returns {string} 完全なURL
   */
  _buildUrl(endpoint) {
    return `${this.baseUrl}${endpoint}`;
  }

  /**
   * リクエストヘッダー構築
   * @private
   * @param {Object} options - リクエストオプション
   * @returns {Promise<Object>} 構築されたヘッダー
   */
  async _buildHeaders(options = {}) {
    const headers = {
      'Content-Type': 'application/json',
      'X-Environment': this.environment,
      ...options.headers
    };

    // CSRFトークンが必要なメソッドの場合は追加
    if (options.method && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(options.method.toUpperCase())) {
      const csrfToken = await this._getCsrfToken();
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }
    }

    return headers;
  }

  /**
   * リクエストオプション構築
   * @private
   * @param {Object} options - 元のオプション
   * @returns {Promise<Object>} 構築されたリクエストオプション
   */
  async _buildRequestOptions(options = {}) {
    return {
      method: options.method || 'POST',
      headers: await this._buildHeaders(options),
      credentials: 'include', // Cookieを含める
      ...options
    };
  }

  /**
   * タイムアウト処理の設定
   * @private
   * @returns {Object} { controller, timeoutId }
   */
  _setupTimeout() {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);
    return { controller, timeoutId };
  }

  /**
   * タイムアウト処理のクリーンアップ
   * @private
   * @param {number} timeoutId - タイムアウトID
   */
  _clearTimeout(timeoutId) {
    clearTimeout(timeoutId);
  }

  /**
   * リクエストログを出力
   * @private
   * @param {string} method - HTTPメソッド
   * @param {string} url - リクエストURL
   * @param {Object} options - リクエストオプション
   */
  _logRequest(method, url, options = {}) {
  }

  /**
   * レスポンスログを出力
   * @private
   * @param {Object} result - レスポンス結果
   */
  _logResponse(result) {
    const logData = {
      status: result.status,
      ok: result.ok,
      hasData: !!result.data,
      timestamp: new Date().toISOString()
    };

    const level = result.ok ? 'log' : 'warn';
    console[level](`${this.logPrefix} Response:`, logData);
  }

  /**
   * HTTPレスポンスからレスポンスオブジェクトを構築
   * @private
   * @param {Response} response - fetchのレスポンス
   * @returns {Promise<Object>} レスポンスオブジェクト
   */
  async _buildResponseObject(response) {
    const result = {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok
    };

    // Content-Typeヘッダーを確認してJSONパース
    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      try {
        result.data = await response.json();
      } catch (parseError) {
        console.warn(`${this.logPrefix} Failed to parse JSON response:`, parseError);
        // JSONパースに失敗してもエラーにはしない
      }
    } else if (contentType?.includes('text/')) {
      try {
        result.text = await response.text();
      } catch (textError) {
        console.warn(`${this.logPrefix} Failed to parse text response:`, textError);
      }
    }

    return result;
  }

  /**
   * HTTPリクエストの実行（フェッチ部分のみ）
   * @private
   * @param {string} url - リクエストURL
   * @param {Object} requestOptions - リクエストオプション
   * @param {AbortController} controller - アボートコントローラー
   * @returns {Promise<Response>} fetchのレスポンス
   */
  async _executeHttpRequest(url, requestOptions, controller) {
    return await fetch(url, { 
      ...requestOptions,
      signal: controller.signal
    });
  }

  /**
   * リクエストエラーの処理
   * @private
   * @param {Error} error - エラーオブジェクト
   * @param {number} timeoutId - タイムアウトID
   * @param {string} endpoint - エンドポイント（ログ用）
   * @throws {Error} 処理されたエラー
   */
  _handleRequestError(error, timeoutId, endpoint) {
    this._clearTimeout(timeoutId);
    
    // タイムアウトエラー
    if (error.name === 'AbortError') {
      console.error(`${this.logPrefix} Request timeout for ${endpoint}`);
      throw new Error('Request timeout');
    }
    
    // ネットワークエラーなど
    console.error(`${this.logPrefix} Request failed for ${endpoint}:`, error);
    throw error;
  }

  /**
   * HTTP リクエスト実行（メイン処理）
   * @protected
   * @param {string} endpoint - エンドポイント
   * @param {Object} options - リクエストオプション
   * @param {boolean} retryWithNewCsrf - CSRFトークンを再取得してリトライするか
   * @returns {Promise<Object>} レスポンスオブジェクト
   */
  async _request(endpoint, options = {}, retryWithNewCsrf = true) {
    const url = this._buildUrl(endpoint);
    const requestOptions = await this._buildRequestOptions(options);
    
    // タイムアウト処理の設定
    const { controller, timeoutId } = this._setupTimeout();

    try {
      // リクエストログ出力
      this._logRequest(requestOptions.method, url, requestOptions);

      // HTTPリクエスト実行
      const response = await this._executeHttpRequest(url, requestOptions, controller);
      
      // タイムアウトクリア
      this._clearTimeout(timeoutId);

      // CSRFトークンエラーの場合、トークンを再取得してリトライ
      if (response.status === 403 && retryWithNewCsrf) {
        const responseData = await response.clone().json().catch(() => ({}));
        
        if (responseData.message?.includes('CSRF') || responseData.error?.includes('CSRF')) {
          console.warn(`${this.logPrefix} CSRF token expired, retrying with new token...`);
          
          // CSRFトークンをクリアして再取得
          this._clearCsrfToken();
          
          // リトライ（retryWithNewCsrfをfalseにして無限ループ防止）
          return await this._request(endpoint, options, false);
        }
      }

      // レスポンスオブジェクトを構築
      const result = await this._buildResponseObject(response);

      // レスポンスログ出力
      this._logResponse(result);

      return result;

    } catch (error) {
      // エラー処理
      this._handleRequestError(error, timeoutId, endpoint);
    }
  }

  /**
   * GETリクエストのヘルパーメソッド
   * @param {string} endpoint - エンドポイント
   * @param {Object} options - 追加オプション
   * @returns {Promise<Object>} レスポンスオブジェクト
   */
  async get(endpoint, options = {}) {
    return this._request(endpoint, {
      ...options,
      method: 'GET'
    });
  }

  /**
   * POSTリクエストのヘルパーメソッド
   * @param {string} endpoint - エンドポイント
   * @param {Object} data - 送信データ
   * @param {Object} options - 追加オプション
   * @returns {Promise<Object>} レスポンスオブジェクト
   */
  async post(endpoint, data = null, options = {}) {
    return this._request(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : options.body
    });
  }

  /**
   * PUTリクエストのヘルパーメソッド
   * @param {string} endpoint - エンドポイント
   * @param {Object} data - 送信データ
   * @param {Object} options - 追加オプション
   * @returns {Promise<Object>} レスポンスオブジェクト
   */
  async put(endpoint, data = null, options = {}) {
    return this._request(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : options.body
    });
  }

  /**
   * PATCHリクエストのヘルパーメソッド
   * @param {string} endpoint - エンドポイント
   * @param {Object} data - 送信データ
   * @param {Object} options - 追加オプション
   * @returns {Promise<Object>} レスポンスオブジェクト
   */
  async patch(endpoint, data = null, options = {}) {
    return this._request(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : options.body
    });
  }

  /**
   * DELETEリクエストのヘルパーメソッド
   * @param {string} endpoint - エンドポイント
   * @param {Object} options - 追加オプション
   * @returns {Promise<Object>} レスポンスオブジェクト
   */
  async delete(endpoint, options = {}) {
    return this._request(endpoint, {
      ...options,
      method: 'DELETE'
    });
  }

  /**
   * 認証付きリクエストのヘルパーメソッド
   * @param {string} endpoint - エンドポイント
   * @param {string} token - 認証トークン
   * @param {Object} options - 追加オプション
   * @returns {Promise<Object>} レスポンスオブジェクト
   */
  async authenticatedRequest(endpoint, token, options = {}) {
    return this._request(endpoint, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        ...options.headers
      }
    });
  }

  /**
   * 設定を動的に変更
   * @param {Object} newConfig
   */
  updateConfig(newConfig) {
    if (newConfig.baseUrl !== undefined) this.baseUrl = newConfig.baseUrl;
    if (newConfig.endpoints) {
      this.endpoints = { 
        ...this.endpoints, 
        ...newConfig.endpoints 
      };
    }
    if (newConfig.timeout) this.timeout = newConfig.timeout;
    if (newConfig.environment) this.environment = newConfig.environment;
  }

  /**
   * 現在の設定を取得（デバッグ用）
   * @returns {Object} 現在の設定
   */
  getConfig() {
    return {
      clientName: this.clientName,
      baseUrl: this.baseUrl,
      endpoints: this.endpoints,
      timeout: this.timeout,
      environment: this.environment,
      usingProxy: this.baseUrl === '',
      hasCsrfToken: !!this._csrfToken
    };
  }

  /**
   * エンドポイント一覧を取得
   * @returns {Object} エンドポイント設定
   */
  getEndpoints() {
    return this.endpoints;
  }

  /**
   * 環境情報を取得
   * @returns {string} 現在の環境
   */
  getEnvironment() {
    return this.environment;
  }

  /**
   * ベースURLを取得
   * @returns {string} ベースURL
   */
  getBaseUrl() {
    return this.baseUrl;
  }

  /**
   * APIヘルスチェック
   * @returns {Promise<boolean>} API が正常かどうか
   */
  async healthCheck() {
    try {
      const result = await this.get(this.endpoints.HEALTH || '/health');
      return result.ok;
    } catch {
      return false;
    }
  }
}

export default BaseApiClient;